import { IPersonalRating, IPersonalRatingRequest } from '../../interfaces';
import { bucketBy } from '../common/ratingUtils';

export const COMMENT_MAX_LENGTH = 1000;

export interface IEmployeeRealEstate {
  realEstateGuid: string;
  realEstateName: string;
  ratingId: number;
}

export interface IEmployee {
  key: string;
  fullName: string;
  positionName: string;
  realEstates: IEmployeeRealEstate[];
}

export interface IPersonalPartnerGroup {
  key: string;
  partnerName: string;
  serviceTypeNames: string[];
  realEstateNames: string[];
  employees: IEmployee[];
}

export interface IEmployeeAnswer {
  rating: number | null;
  skipped: boolean;
  comment: string;
}

export type PersonalRatingState = Record<string, IEmployeeAnswer>;

const EMPTY_ANSWER: IEmployeeAnswer = { rating: null, skipped: false, comment: '' };

const byName = (a: string, b: string) => a.localeCompare(b, 'ru');

const isFilled = (value?: string): value is string => typeof value === 'string' && value !== '';

const uniqueSorted = (values: (string | undefined)[]) => Array.from(new Set(values.filter(isFilled))).sort(byName);

const fullNameOf = (row: IPersonalRating) => row.fullName || [row.lastName, row.firstName].filter(Boolean).join(' ');

function toEmployee(partnerGuid: string, rows: IPersonalRating[]): IEmployee {
  const [first] = rows;
  return {
    key: `${partnerGuid}|${first.personalId}`,
    fullName: fullNameOf(first),
    positionName: first.positionName ?? '',
    realEstates: rows
      .map((row) => ({
        realEstateGuid: row.realEstateGuid ?? '',
        realEstateName: row.realEstateName ?? '',
        ratingId: row.realEstatePersonalRatingId ?? 0,
      }))
      .sort((a, b) => byName(a.realEstateName, b.realEstateName)),
  };
}

function toGroup(partnerGuid: string, rows: IPersonalRating[]): IPersonalPartnerGroup {
  const employees = Array.from(bucketBy(rows, (row) => String(row.personalId)).values())
    .map((employeeRows) => toEmployee(partnerGuid, employeeRows))
    .sort((a, b) => byName(a.fullName, b.fullName));
  return {
    key: partnerGuid,
    partnerName: rows[0].partnerName ?? '',
    serviceTypeNames: uniqueSorted(rows.map(({ serviceTypeName }) => serviceTypeName)),
    realEstateNames: uniqueSorted(rows.map(({ realEstateName }) => realEstateName)),
    employees,
  };
}

/** Строки «сотрудник × ЖК» → партнёр → сотрудник (одна карточка на все его ЖК у этого партнёра). */
export const groupPersonalRatings = (rows: IPersonalRating[]): IPersonalPartnerGroup[] =>
  Array.from(bucketBy(rows, (row) => row.partnerGuid ?? '').entries())
    .map(([partnerGuid, partnerRows]) => toGroup(partnerGuid, partnerRows))
    .sort((a, b) => byName(a.partnerName, b.partnerName));

export const answerOf = (state: PersonalRatingState, key: string): IEmployeeAnswer => state[key] ?? EMPTY_ANSWER;

export const isAnswered = (answer: IEmployeeAnswer) => answer.skipped || answer.rating !== null;

export const setRating = (state: PersonalRatingState, key: string, rating: number): PersonalRatingState => ({
  ...state,
  [key]: { ...answerOf(state, key), rating, skipped: false },
});

export function toggleSkip(state: PersonalRatingState, key: string): PersonalRatingState {
  const answer = answerOf(state, key);
  return { ...state, [key]: { ...answer, rating: null, skipped: !answer.skipped } };
}

export const setComment = (state: PersonalRatingState, key: string, comment: string): PersonalRatingState => ({
  ...state,
  [key]: { ...answerOf(state, key), comment },
});

export const restoreAnswer = (state: PersonalRatingState, key: string, answer: IEmployeeAnswer) => ({
  ...state,
  [key]: answer,
});

export const answeredCount = (group: IPersonalPartnerGroup, state: PersonalRatingState) =>
  group.employees.filter(({ key }) => isAnswered(answerOf(state, key))).length;

export const remainingCount = (groups: IPersonalPartnerGroup[], state: PersonalRatingState) =>
  groups.reduce((acc, group) => acc + group.employees.length - answeredCount(group, state), 0);

export const skippedCount = (groups: IPersonalPartnerGroup[], state: PersonalRatingState) =>
  groups.reduce((acc, group) => acc + group.employees.filter(({ key }) => answerOf(state, key).skipped).length, 0);

export function firstUnanswered(groups: IPersonalPartnerGroup[], state: PersonalRatingState) {
  const groupIndex = groups.findIndex((group) => answeredCount(group, state) < group.employees.length);
  if (groupIndex < 0) return null;
  const employee = groups[groupIndex].employees.find(({ key }) => !isAnswered(answerOf(state, key)));
  return employee ? { groupIndex, key: employee.key } : null;
}

function employeeRequests(employee: IEmployee, answer: IEmployeeAnswer): IPersonalRatingRequest[] {
  const comment = answer.skipped ? null : answer.comment.trim() || null;
  return employee.realEstates.map(({ ratingId }) => ({
    realEstatePersonalRatingId: ratingId,
    rating: answer.skipped ? null : answer.rating,
    skipped: answer.skipped,
    comment,
  }));
}

/** Оценка сотрудника уходит в каждую его строку (по ЖК). */
export const buildPersonalRequests = (groups: IPersonalPartnerGroup[], state: PersonalRatingState) =>
  groups.flatMap((group) =>
    group.employees.flatMap((employee) => employeeRequests(employee, answerOf(state, employee.key))),
  );
